import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RECENTLY_VIEWED_VIEW_ID } from "@/hooks/use-object-list-views";
import { cn } from "@/lib/utils";
import { ListView } from "@/services/list-view";
import { Check, Clock, Pin, Search, Globe, Shield, Lock } from "lucide-react";
import React, { useState } from "react";
import { Button } from "../ui/button";

interface ViewsSelectorProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    views: ListView[];
    currentView: ListView;
    onViewChange: (view: ListView) => void;
    triggerElement: React.ReactNode;
    onPinView?: (id: number) => Promise<void>;
    onUnpinView?: (id: number) => Promise<void>;
    referenceTypeId?: number;
    recentlyViewedView?: ListView | null;
}

export function ViewsSelectorComponent({
    open,
    onOpenChange,
    views,
    currentView,
    onViewChange,
    triggerElement,
    onPinView,
    onUnpinView,
    recentlyViewedView,
}: ViewsSelectorProps) {
    const [searchTerm, setSearchTerm] = useState("");

    // Filtrer les vues selon le terme de recherche
    const filteredViews = views.filter(view => view.name.toLowerCase().includes(searchTerm.toLowerCase()));

    const getSharingModeIcon = (mode: string) => {
        switch (mode) {
            case "public":
                return <Globe className="h-4 w-4 text-green-600" />;
            case "protected":
                return <Shield className="h-4 w-4 text-orange-600" />;
            case "private":
            default:
                return <Lock className="h-4 w-4 text-gray-500" />;
        }
    };

    const handleViewSelect = (view: ListView) => {
        onViewChange(view);
        onOpenChange(false);
    };

    const handlePinToggle = async (view: ListView, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!view.id) return;
        if (view.id === RECENTLY_VIEWED_VIEW_ID) return;
        if (typeof view.id !== "number" || Number.isNaN(Number(view.id))) return;

        try {
            if (view.is_pinned) {
                await onUnpinView?.(view.id);
            } else {
                await onPinView?.(view.id);
            }
        } catch (error) {
            console.error("Failed to toggle pin:", error);
        }
    };

    return (
        <Popover open={open} onOpenChange={onOpenChange}>
            <PopoverTrigger asChild>{triggerElement}</PopoverTrigger>
            <PopoverContent className="w-96 p-0" align="start">
                <div className="flex flex-col">
                    {/* Barre de recherche */}
                    <div className="relative p-2">
                        <Search className="absolute left-5 top-1/2  h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                        <Input placeholder="Search a list view..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="h-9 pl-10" />
                    </div>

                    {/* Liste des vues */}
                    <ScrollArea className="h-72">
                        <div className="p-2">
                            {recentlyViewedView && (
                                <>
                                    <div
                                        className={cn(
                                            "group relative mb-1 rounded-md border border-transparent hover:border-gray-200",
                                            currentView.id === recentlyViewedView.id && "border-gray-200 bg-gray-50"
                                        )}
                                    >
                                        <Button variant="ghost" className="h-auto w-full justify-start p-3" onClick={() => handleViewSelect(recentlyViewedView)}>
                                            <div className="flex w-full items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <Clock className="h-4 w-4 text-gray-500" />
                                                    <span className="text-sm font-medium">{recentlyViewedView.name}</span>
                                                </div>
                                                {currentView.id === recentlyViewedView.id && <Check className="h-4 w-4 text-azure-blue" />}
                                            </div>
                                        </Button>
                                    </div>
                                    <div className="my-2 border-t border-gray-200" />
                                </>
                            )}

                            {filteredViews.length > 0 ? (
                                filteredViews
                                    .sort((a, b) => {
                                        if (a.is_pinned && !b.is_pinned) return -1;
                                        if (!a.is_pinned && b.is_pinned) return 1;
                                        return Number(a.id) - Number(b.id);
                                    })
                                    .map(view => (
                                        <div
                                            key={view.id}
                                            className={cn("group relative mb-1 rounded-md border border-transparent hover:border-gray-200", currentView.id === view.id && "border-gray-200 bg-gray-50")}
                                        >
                                            <Button variant="ghost" className="h-auto w-full justify-start p-3" onClick={() => handleViewSelect(view)}>
                                                <div className="flex w-full items-center justify-between">
                                                    <div className="flex flex-col items-start">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-medium">{view.name}</span>
                                                            {getSharingModeIcon(view.sharing_mode || "private")}
                                                        </div>
                                                        <span className="text-xs text-gray-500">
                                                            {view.columns.length} columns
                                                            {view.created_by && ` • by ${view.created_by.first_name} ${view.created_by.last_name}`}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {currentView.id === view.id && <Check className="h-4 w-4 text-azure-blue" />}
                                                        <Pin
                                                            className={cn("h-4 w-4 cursor-pointer", view.is_pinned ? "text-azure-blue" : "text-gray-400 hover:text-azure-blue")}
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                handlePinToggle(view, e);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </Button>
                                        </div>
                                    ))
                            ) : (
                                <div className="py-6 text-center text-gray-500">
                                    <Search className="mx-auto mb-2 h-8 w-8 opacity-50" />
                                    <p className="text-sm">No list view found</p>
                                    <p className="text-xs">Try another search term</p>
                                </div>
                            )}
                        </div>
                    </ScrollArea>
                </div>
            </PopoverContent>
        </Popover>
    );
}
